import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { FiltroDealers } from './filtro-dealer';
import { VeiculoFrequenteFiltroService } from './veiculo-frequente-filtro.service';
import { VeiculoFrequenteService } from './veiculo-frequente.service';
import { Utils } from '../../../shared/utils/utils';

@Component({
  selector: 'app-veiculo-frequente',
  templateUrl: './veiculo-frequente.component.html',
  styleUrls: ['./veiculo-frequente.component.scss']
})
export class VeiculoFrequenteComponent implements OnInit {

  /**
   * Janela fixa do relatorio, em dias.
   *
   * Os campos de data foram removidos da tela: a consulta considera sempre
   * as ordens dos ultimos 90 dias, regra aplicada no backend. A constante
   * serve apenas para montar o link do RMPV detalhado com o mesmo periodo.
   */
  private readonly JANELA_DIAS = 90;

  form: FormGroup;

  filtrosDealers: FiltroDealers;

  displayFiltrosDealers = false;
  displayFiltros = false;

  loading = false;
  cargaTela = false;

  dealersDados: any[] = [];
  rows: any[] = [];

  cols: any[] = [];
  selectedColumns: any[] = [];

  // Dialog de observacoes
  displayObservacoes = false;
  chassiSelecionado = '';
  numeroOsSelecionado = '';
  historicoAcompanhamento: any[] = [];
  novaObservacao = '';
  carregandoHistorico = false;
  salvandoObservacao = false;

  constructor(
    private service: VeiculoFrequenteService,
    private filtroService: VeiculoFrequenteFiltroService,
    private formBuilder: FormBuilder,
    private datePipe: DatePipe,
    private toastr: ToastrService,
    private router: Router,
    public translate: TranslateService
  ) {
    this.filtrosDealers = new FiltroDealers(this.filtroService);
  }

  ngOnInit() {
    this.criarFormulario();
    this.criarColunas();
    this.filtrosDealers.carregar();

    this.rows = this.dealersDados;
  }

  private criarFormulario() {
    this.form = this.formBuilder.group({
      numeroChassi: ['']
    });
  }

  private criarColunas() {
    this.cols = [
      { field: 'numeroChassi', header: 'Chassi' },
      { field: 'modelo', header: 'Modelo' },
      { field: 'dataHandover', header: 'Data Handover' },
      { field: 'dealer', header: 'Dealer' },
      { field: 'numeroOs', header: 'Número O.S' },
      { field: 'tipoOs', header: 'Tipo O.S' },
      { field: 'nomeCliente', header: 'Nome Cliente' },
      { field: 'status', header: 'Status' },
      { field: 'dataAbertura', header: 'Data Abertura' },
      { field: 'dataLiberacao', header: 'Data Liberação' },
      { field: 'dataFechamento', header: 'Data Fechamento' },
      { field: 'iniciado', header: 'Iniciado' },
      { field: 'finalizado', header: 'Finalizado' },
      { field: 'observacoes', header: 'Observações' }
    ];

    this.selectedColumns = this.cols;
  }

  filtroAPI() {
    this.loading = true;
    this.cargaTela = true;

    const dealersSelecionados = this.getDealersSelecionados();
    const numeroChassi = this.form.get('numeroChassi').value || '';

    this.service.getDados(
      dealersSelecionados,
      numeroChassi
    ).subscribe(
      response => {
        this.dealersDados = this.normalizarResponse(response);
        this.dealersDados.forEach(item => Utils.sanitizarRegistro(item));
        this.rows = this.dealersDados;
        this.loading = false;
        this.cargaTela = false;
        this.displayFiltros = false;
      },
      error => {
        this.dealersDados = [];
        this.rows = [];
        this.loading = false;
        this.cargaTela = false;
        this.toastr.error('Erro ao buscar relatório de veículo frequente.');
      }
    );
  }

  private normalizarResponse(response: any): any[] {
    if (!response) {
      return [];
    }

    if (Array.isArray(response)) {
      return response;
    }

    if (response.itens && Array.isArray(response.itens)) {
      return response.itens;
    }

    if (response.dados && Array.isArray(response.dados)) {
      return response.dados;
    }

    if (response.lista && Array.isArray(response.lista)) {
      return response.lista;
    }

    return [];
  }

  private getDealersSelecionados(): string {
    const selecionados = this.filtrosDealers.selecionados();

    if (!selecionados || selecionados.length === 0) {
      return '';
    }

    return selecionados.map(dealer => dealer.id).join(',');
  }

  selecionarDealer() {
    this.displayFiltrosDealers = true;
  }

  onAdicionarDealerFiltro(dealer: any) {
    dealer.selecionado = true;
  }

  removerDealer(dealer: any) {
    dealer.selecionado = false;
  }

  exibirFiltro() {
    this.displayFiltros = true;
  }

  getListagemComFiltros(): boolean {
    const numeroChassi = this.form.get('numeroChassi').value;

    return numeroChassi !== null
      && numeroChassi !== undefined
      && numeroChassi.toString().trim() !== '';
  }

  limparFiltros() {
    this.form.reset({
      numeroChassi: ''
    });
  }

  /**
   * Abre o RMPV detalhado usando a mesma janela de 90 dias do relatorio.
   */
  abrirRmpvDetalhado(numeroChassi: string) {
    if (!numeroChassi) {
      return;
    }

    const dataFinal = new Date();

    const dataInicial = new Date();
    dataInicial.setDate(dataInicial.getDate() - this.JANELA_DIAS);

    const dataInicialFormatada = this.datePipe.transform(dataInicial, 'yyyy-MM-dd');
    const dataFinalFormatada = this.datePipe.transform(dataFinal, 'yyyy-MM-dd');

    this.router.navigate(['/rmpv/detalhado'], {
      queryParams: {
        numeroChassi: numeroChassi,
        dataInicial: dataInicialFormatada,
        dataFinal: dataFinalFormatada
      }
    });
  }

  // ------------------------------------------------------------------
  // Iniciado e Finalizado
  //
  // O acompanhamento e por chassi: ao marcar uma linha, todas as ordens
  // do mesmo veiculo passam a exibir o estado marcado. Uma vez marcado,
  // nao pode ser desmarcado - o backend recusa nova tentativa.
  // ------------------------------------------------------------------

  estaIniciado(item: any): boolean {
    return item && item.iniciado === 'S';
  }

  estaFinalizado(item: any): boolean {
    return item && item.finalizado === 'S';
  }

  tooltipIniciado(item: any): string {
    if (!this.estaIniciado(item)) {
      return 'Marcar como iniciado';
    }

    return `Iniciado por ${item.iniciadoPor || '-'} em ${item.iniciadoEm || '-'}`;
  }

  tooltipFinalizado(item: any): string {
    if (!this.estaFinalizado(item)) {
      return 'Marcar como finalizado';
    }

    return `Finalizado por ${item.finalizadoPor || '-'} em ${item.finalizadoEm || '-'}`;
  }

  marcarIniciado(item: any) {
    if (this.estaIniciado(item)) {
      return;
    }

    this.service.marcarIniciado(item.numeroChassi, item.numeroOs).subscribe(
      () => {
        this.toastr.success('Veículo marcado como iniciado.');
        this.filtroAPI();
      },
      erro => {
        this.toastr.warning(this.extrairMensagem(erro));
      }
    );
  }

  marcarFinalizado(item: any) {
    if (this.estaFinalizado(item)) {
      return;
    }

    this.service.marcarFinalizado(item.numeroChassi, item.numeroOs).subscribe(
      () => {
        this.toastr.success('Veículo marcado como finalizado.');
        this.filtroAPI();
      },
      erro => {
        this.toastr.warning(this.extrairMensagem(erro));
      }
    );
  }

  // ------------------------------------------------------------------
  // Observacoes
  //
  // As observacoes apenas se acumulam. O dialog mostra o historico
  // completo do chassi e permite incluir uma nova.
  // ------------------------------------------------------------------

  abrirObservacoes(item: any) {
    this.chassiSelecionado = item.numeroChassi;
    this.numeroOsSelecionado = item.numeroOs;
    this.novaObservacao = '';
    this.historicoAcompanhamento = [];
    this.displayObservacoes = true;

    this.carregarHistorico();
  }

  private carregarHistorico() {
    this.carregandoHistorico = true;

    this.service.buscarHistorico(this.chassiSelecionado).subscribe(
      res => {
        this.historicoAcompanhamento = (res && res.itens) ? res.itens : [];
        this.historicoAcompanhamento.forEach(item => Utils.sanitizarRegistro(item));
        this.carregandoHistorico = false;
      },
      () => {
        this.historicoAcompanhamento = [];
        this.carregandoHistorico = false;
        this.toastr.error('Erro ao buscar o histórico de acompanhamento.');
      }
    );
  }

  salvarObservacao() {
    if (!this.novaObservacao || !this.novaObservacao.trim()) {
      this.toastr.warning('Informe o texto da observação.');
      return;
    }

    this.salvandoObservacao = true;

    this.service.incluirObservacao(
      this.chassiSelecionado,
      this.numeroOsSelecionado,
      this.novaObservacao.trim()
    ).subscribe(
      () => {
        this.salvandoObservacao = false;
        this.novaObservacao = '';
        this.toastr.success('Observação incluída.');
        this.carregarHistorico();
        this.filtroAPI();
      },
      erro => {
        this.salvandoObservacao = false;
        this.toastr.warning(this.extrairMensagem(erro));
      }
    );
  }

  fecharObservacoes() {
    this.displayObservacoes = false;
    this.chassiSelecionado = '';
    this.numeroOsSelecionado = '';
    this.historicoAcompanhamento = [];
    this.novaObservacao = '';
  }

  descricaoEvento(evento: any): string {
    if (evento.status === 'INICIADO') {
      return 'Marcou como iniciado';
    }

    if (evento.status === 'FINALIZADO') {
      return 'Marcou como finalizado';
    }

    return evento.texto || '';
  }

  private extrairMensagem(erro: any): string {
    return (erro && erro.error && erro.error.erro)
      ? erro.error.erro
      : 'Não foi possível concluir a operação.';
  }

  // ------------------------------------------------------------------
  // Exportacoes
  // ------------------------------------------------------------------

  /**
   * Exportacao da listagem, incluindo as tres colunas novas e os logs de
   * quem marcou e quando.
   */
  exportExcel() {
    if (!this.dealersDados || this.dealersDados.length === 0) {
      this.toastr.warning('Não existem dados para exportar.');
      return;
    }

    const colunas = this.selectedColumns.filter(col => col.field !== 'observacoes');

    const header = colunas.map(col => col.header)
      .concat(['Iniciado Por', 'Iniciado Em', 'Finalizado Por', 'Finalizado Em', 'Qtd. Observações'])
      .join(';');

    const linhas = this.dealersDados.map(item => {
      const valores = colunas.map(col => {
        if (col.field === 'iniciado') {
          return this.estaIniciado(item) ? 'Sim' : 'Nao';
        }

        if (col.field === 'finalizado') {
          return this.estaFinalizado(item) ? 'Sim' : 'Nao';
        }

        return item[col.field] !== null && item[col.field] !== undefined ? Utils.sanitizarTexto(item[col.field]) : '';
      });

      valores.push(Utils.sanitizarTexto(item.iniciadoPor || ''));
      valores.push(item.iniciadoEm || '');
      valores.push(Utils.sanitizarTexto(item.finalizadoPor || ''));
      valores.push(item.finalizadoEm || '');
      valores.push(item.qtdObservacoes != null ? item.qtdObservacoes : 0);

      return valores.join(';');
    });

    this.baixarCsv([header, ...linhas].join('\n'), 'veiculo-frequente.csv');
  }

  /**
   * Segunda extracao: todos os acompanhamentos ja registrados, inclusive
   * de chassis que sairam da listagem por estarem fora dos 90 dias.
   */
  exportAcompanhamentos() {
    this.service.listarAcompanhamentos().subscribe(
      res => {
        const itens = (res && res.itens) ? res.itens : [];

        if (itens.length === 0) {
          this.toastr.warning('Não existem acompanhamentos registrados.');
          return;
        }

        const header = ['Chassi', 'Número O.S', 'Sequência', 'Data/Hora', 'Usuário', 'Ação', 'Texto'].join(';');

        const linhas = itens.map(i => [
          i.numeroChassi || '',
          i.numeroOs || '',
          i.sequencia != null ? i.sequencia : '',
          i.dataHora || '',
          Utils.sanitizarTexto(i.usuario || ''),
          Utils.sanitizarTexto(i.status || ''),
          Utils.sanitizarTexto((i.texto || '').replace(/;/g, ',').replace(/\n/g, ' '))
        ].join(';'));

        this.baixarCsv([header, ...linhas].join('\n'), 'veiculo-frequente-acompanhamentos.csv');
      },
      () => {
        this.toastr.error('Erro ao buscar os acompanhamentos.');
      }
    );
  }

  private baixarCsv(conteudo: string, nomeArquivo: string) {
    const blob = new Blob(['\uFEFF' + conteudo], { type: 'text/csv;charset=utf-8;' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = nomeArquivo;
    link.click();
  }
}